import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ModulesService, type UpdateModuleDto, type Module } from '../../services/security/modules.service';
import { NotificationToast } from '../ui/NotificationToast';
import { Grid3X3, Settings, Users, Eye, Home, Shield, Database, FileText, BarChart3, Calendar, Mail, Bell, Lock, Key, CreditCard, Package, Truck, MapPin, Phone, Building, UserCheck, UserX, Folder, File, Search, Filter, Download, Upload, Edit, Trash2, Plus, Minus } from 'lucide-react';

const updateModuleSchema = z.object({
  nombre: z.string().min(1, 'Nombre es requerido').min(3, 'Mínimo 3 caracteres'),
  descripcion: z.string().optional(),
  ruta: z.string().optional(),
  icono: z.string().optional(),
  orden: z.number().min(0, 'El orden debe ser mayor o igual a 0').optional(),
  tipo: z.enum(['navigation', 'drawer']),
  moduloPadreId: z.string().optional(),
  activo: z.boolean().optional()
});

type UpdateModuleFormData = z.infer<typeof updateModuleSchema>;

interface EditModuleDrawerProps {
  isOpen: boolean;
  module: Module | null;
  onClose: () => void;
  onSuccess?: () => void;
  onUpdateResult?: (success: boolean, message: string) => void;
}

const ICON_OPTIONS = [
  { value: 'grid3x3', label: 'Cuadrícula', icon: Grid3X3 },
  { value: 'settings', label: 'Configuración', icon: Settings },
  { value: 'users', label: 'Usuarios', icon: Users },
  { value: 'eye', label: 'Vista', icon: Eye },
  { value: 'home', label: 'Inicio', icon: Home },
  { value: 'shield', label: 'Seguridad', icon: Shield },
  { value: 'database', label: 'Base de Datos', icon: Database },
  { value: 'filetext', label: 'Documentos', icon: FileText },
  { value: 'barchart3', label: 'Reportes', icon: BarChart3 },
  { value: 'calendar', label: 'Calendario', icon: Calendar },
  { value: 'mail', label: 'Correo', icon: Mail },
  { value: 'bell', label: 'Notificaciones', icon: Bell },
  { value: 'lock', label: 'Bloquear', icon: Lock },
  { value: 'key', label: 'Llaves', icon: Key },
  { value: 'creditcard', label: 'Pagos', icon: CreditCard },
  { value: 'package', label: 'Productos', icon: Package },
  { value: 'truck', label: 'Envíos', icon: Truck },
  { value: 'mappin', label: 'Ubicación', icon: MapPin },
  { value: 'phone', label: 'Teléfono', icon: Phone },
  { value: 'building', label: 'Empresa', icon: Building },
  { value: 'usercheck', label: 'Verificar Usuario', icon: UserCheck },
  { value: 'userx', label: 'Usuario Bloqueado', icon: UserX },
  { value: 'folder', label: 'Carpeta', icon: Folder },
  { value: 'file', label: 'Archivo', icon: File },
  { value: 'search', label: 'Buscar', icon: Search },
  { value: 'filter', label: 'Filtrar', icon: Filter },
  { value: 'download', label: 'Descargar', icon: Download },
  { value: 'upload', label: 'Subir', icon: Upload },
  { value: 'edit', label: 'Editar', icon: Edit },
  { value: 'trash2', label: 'Eliminar', icon: Trash2 },
  { value: 'plus', label: 'Agregar', icon: Plus },
  { value: 'minus', label: 'Quitar', icon: Minus }
];

const TIPO_OPTIONS = [
  { value: 'navigation', label: 'Navegación' },
  { value: 'drawer', label: 'Modal/Drawer' }
];

export const EditModuleDrawer: React.FC<EditModuleDrawerProps> = ({
  isOpen,
  module,
  onClose,
  onSuccess,
  onUpdateResult
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [updatedModule, setUpdatedModule] = useState<any>(null);
  
  // Estados para notificaciones
  const [showNotification, setShowNotification] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    setValue,
    watch
  } = useForm<UpdateModuleFormData>({
    resolver: zodResolver(updateModuleSchema),
    defaultValues: {
      activo: true,
      orden: 0,
      tipo: 'navigation'
    }
  });

  const watchedTipo = watch('tipo');
  const watchedIcon = watch('icono');

  useEffect(() => {
    if (isOpen && module) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10);
      loadModuleData();
      return () => clearTimeout(timer);
    } else if (!isOpen) {
      setIsVisible(false);
      setTimeout(() => {
        setIsLoading(false);
        setErrorMessage(null);
        setUpdatedModule(null);
        reset();
      }, 300);
    }
  }, [isOpen, module, reset]);

  const loadModuleData = () => {
    if (module) {
      // Cargar datos del módulo en el formulario
      setValue('nombre', module.nombre);
      setValue('descripcion', module.descripcion || '');
      setValue('ruta', module.ruta || '');
      setValue('icono', module.icono || 'grid3x3');
      setValue('orden', module.orden);
      setValue('tipo', module.tipo);
      setValue('moduloPadreId', module.moduloPadreId || '');
      setValue('activo', module.activo);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const onSubmit = async (data: UpdateModuleFormData) => {
    if (!module) return;
    
    setIsLoading(true);
    setErrorMessage(null);
    
    try {
      const moduleData: UpdateModuleDto = {
        nombre: data.nombre,
        descripcion: data.descripcion,
        ruta: data.ruta,
        icono: data.icono,
        orden: data.orden,
        tipo: data.tipo,
        moduloPadreId: data.moduloPadreId || undefined,
        activo: data.activo
      };

      const result = await ModulesService.updateModule(module.id, moduleData);
      
      setUpdatedModule({
        id: result.id,
        nombre: result.nombre,
        descripcion: result.descripcion,
        tipo: result.tipo,
        activo: result.activo
      });

      const message = `El módulo ${result.nombre} fue actualizado exitosamente.`;
      
      setNotificationConfig({
        title: '¡Módulo actualizado!',
        message,
        type: 'success'
      });
      setShowNotification(true);

      if (onUpdateResult) {
        onUpdateResult(true, message);
      }

      setTimeout(() => {
        handleClose();
      }, 2000);
      
      if (onSuccess) {
        onSuccess();
      }
      
    } catch (error) {
      console.error('Error al actualizar módulo:', error);
      const errorMsg = error instanceof Error ? error.message : 'Error desconocido al actualizar módulo';
      setErrorMessage(errorMsg);
      
      setNotificationConfig({
        title: 'Error al actualizar módulo',
        message: errorMsg,
        type: 'error'
      });
      setShowNotification(true);
      
      if (onUpdateResult) {
        onUpdateResult(false, errorMsg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinue = () => {
    handleClose();
  };

  const getSelectedIcon = () => {
    const selectedIcon = ICON_OPTIONS.find(icon => icon.value === watchedIcon);
    return selectedIcon || ICON_OPTIONS[0];
  };

  if (!isOpen || !module) return null;

  return (
    <div className="fixed left-0 right-0 bottom-0 top-0 z-[60] overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute inset-0 cursor-pointer"
          onClick={handleClose}
          aria-label="Cerrar drawer"
        />
        
        <section className={`absolute right-0 top-0 h-full w-full max-w-4xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div 
            className="h-full flex flex-col bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 bg-gray-100 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xs font-medium text-gray-900">
                    Editar Módulo
                  </h2>
                  <p className="text-xs text-gray-600 mt-1">
                    Modificando: {module.nombre}
                  </p>
                </div>
                <button
                  onClick={handleClose}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {errorMessage && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <svg className="w-5 h-5 text-red-400 mr-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-red-800 text-xs">{errorMessage}</p>
                  </div>
                </div>
              )}

              {!updatedModule ? (
                <>
                  {/* Instrucciones */}
                  <div className="mb-6">
                    <h3 className="text-xs font-medium text-gray-900 mb-3">
                      Modificar información del módulo
                    </h3>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <ul className="text-xs text-blue-800 space-y-1">
                        <li>• Modifica los campos que deseas actualizar</li>
                        <li>• El nombre del módulo debe ser único en el sistema</li>
                        <li>• Los cambios se aplicarán inmediatamente</li>
                        <li>• Verifica que la ruta sea correcta para módulos de navegación</li>
                      </ul>
                    </div>
                  </div>

                  {/* Formulario */}
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {/* Información básica */}
                    <div>
                      <h3 className="text-xs font-medium text-gray-900 mb-3">
                        Información Básica
                      </h3>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">
                            Nombre del Módulo *
                          </label>
                          <input
                            type="text"
                            {...register('nombre')}
                            placeholder="Ej: Gestión de Usuarios, Configuración, Dashboard"
                            className={`w-full px-4 py-3 border rounded-lg transition-all ${
                              errors.nombre
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500/20'
                            } focus:ring-4 focus:outline-none`}
                          />
                          {errors.nombre && (
                            <p className="mt-1 text-xs text-red-500">{errors.nombre.message}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">
                            Descripción
                          </label>
                          <textarea
                            {...register('descripcion')}
                            placeholder="Describe la funcionalidad y propósito de este módulo"
                            rows={3}
                            className={`w-full px-4 py-3 border rounded-lg transition-all ${
                              errors.descripcion
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500/20'
                            } focus:ring-4 focus:outline-none resize-none`}
                          />
                          {errors.descripcion && (
                            <p className="mt-1 text-xs text-red-500">{errors.descripcion.message}</p>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                              Tipo *
                            </label>
                            <Controller
                              name="tipo"
                              control={control}
                              render={({ field }) => (
                                <select
                                  {...field}
                                  className={`w-full px-4 py-3 border rounded-lg transition-all ${
                                    errors.tipo
                                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500/20'
                                  } focus:ring-4 focus:outline-none`}
                                >
                                  {TIPO_OPTIONS.map((tipo) => (
                                    <option key={tipo.value} value={tipo.value}>
                                      {tipo.label}
                                    </option>
                                  ))}
                                </select>
                              )}
                            />
                            {errors.tipo && (
                              <p className="mt-1 text-xs text-red-500">{errors.tipo.message}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                              Orden
                            </label>
                            <input
                              type="number"
                              min="0"
                              {...register('orden', { valueAsNumber: true })}
                              placeholder="0"
                              className={`w-full px-4 py-3 border rounded-lg transition-all ${
                                errors.orden
                                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                  : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500/20'
                              } focus:ring-4 focus:outline-none`}
                            />
                            {errors.orden && (
                              <p className="mt-1 text-xs text-red-500">{errors.orden.message}</p>
                            )}
                          </div>
                        </div>

                        {watchedTipo === 'navigation' && (
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                              Ruta
                            </label>
                            <input
                              type="text"
                              {...register('ruta')}
                              placeholder="/ruta/del/modulo"
                              className={`w-full px-4 py-3 border rounded-lg transition-all ${
                                errors.ruta
                                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                  : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500/20'
                              } focus:ring-4 focus:outline-none`}
                            />
                            {errors.ruta && (
                              <p className="mt-1 text-xs text-red-500">{errors.ruta.message}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Configuración visual */}
                    <div>
                      <h3 className="text-xs font-medium text-gray-900 mb-3">
                        Configuración Visual
                      </h3>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2">
                          Icono
                        </label>
                        <Controller
                          name="icono"
                          control={control}
                          render={({ field }) => (
                            <div className="space-y-3">
                              {/* Preview del ícono seleccionado */}
                              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                                {(() => {
                                  const selectedIcon = getSelectedIcon();
                                  const IconComponent = selectedIcon.icon;
                                  return (
                                    <>
                                      <IconComponent className="w-6 h-6 text-blue-600" />
                                      <span className="text-xs font-medium text-gray-700">
                                        {selectedIcon.label}
                                      </span>
                                    </>
                                  );
                                })()}
                              </div>
                              
                              {/* Selector de iconos */}
                              <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 max-h-64 overflow-y-auto p-2 border border-gray-200 rounded-lg bg-gray-50">
                                {ICON_OPTIONS.map((iconOption) => {
                                  const IconComponent = iconOption.icon;
                                  const isSelected = field.value === iconOption.value;
                                  
                                  return (
                                    <button
                                      key={iconOption.value}
                                      type="button"
                                      onClick={() => field.onChange(iconOption.value)}
                                      className={`p-2 rounded-md border transition-all group relative ${
                                        isSelected
                                          ? 'border-blue-500 bg-blue-100 ring-1 ring-blue-500/30'
                                          : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50 bg-white'
                                      }`}
                                      title={iconOption.label}
                                    >
                                      <div className="flex flex-col items-center space-y-1">
                                        <IconComponent className={`w-4 h-4 ${
                                          isSelected ? 'text-blue-600' : 'text-gray-500 group-hover:text-blue-500'
                                        }`} />
                                        <span className={`text-xs font-medium leading-tight text-center ${
                                          isSelected ? 'text-blue-800' : 'text-gray-600'
                                        }`}>
                                          {iconOption.label.length > 8 ? iconOption.label.substring(0, 8) + '...' : iconOption.label}
                                        </span>
                                      </div>
                                      
                                      {/* Tooltip para mostrar el nombre completo */}
                                      {iconOption.label.length > 8 && (
                                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                          {iconOption.label}
                                        </div>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        />
                      </div>
                    </div>

                    {/* Estado */}
                    <div>
                      <h3 className="text-xs font-medium text-gray-900 mb-3">
                        Estado
                      </h3>
                      
                      <div>
                        <Controller
                          name="activo"
                          control={control}
                          render={({ field }) => (
                            <label className="flex items-center space-x-3">
                              <input
                                type="checkbox"
                                checked={field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <span className="text-xs font-medium text-gray-700">
                                Módulo activo (disponible para asignar a roles)
                              </span>
                            </label>
                          )}
                        />
                      </div>
                    </div>
                  </form>
                </>
              ) : (
                /* Resultados de la actualización */
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="mx-auto h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-xs font-medium text-gray-900 mb-2">
                      Módulo actualizado exitosamente
                    </h3>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Información actualizada</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Nombre:</span>
                        <span className="text-xs font-medium text-gray-900">{updatedModule.nombre}</span>
                      </div>
                      {updatedModule.descripcion && (
                        <div className="flex justify-between">
                          <span className="text-xs text-gray-500">Descripción:</span>
                          <span className="text-xs font-medium text-gray-900">{updatedModule.descripcion}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Tipo:</span>
                        <span className="text-xs font-medium text-gray-900">
                          {updatedModule.tipo === 'navigation' ? 'Navegación' : 'Modal/Drawer'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Estado:</span>
                        <span className="text-xs font-medium text-gray-900">
                          {updatedModule.activo ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">ID:</span>
                        <span className="text-xs font-mono text-gray-900">{updatedModule.id}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex">
                      <svg className="w-5 h-5 text-blue-400 mr-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-xs text-blue-800 font-medium">Cambios aplicados</p>
                        <p className="text-xs text-blue-700 mt-1">Las modificaciones han sido guardadas y están disponibles inmediatamente en el sistema.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              {!updatedModule ? (
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="flex-1 bg-white text-gray-700 border border-gray-300 px-3 py-2 text-xs rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    onClick={handleSubmit(onSubmit)}
                    disabled={isLoading}
                    className="flex-1 bg-blue-600 text-white px-3 py-2 text-xs rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                  >
                    {isLoading ? 'Actualizando Módulo...' : 'Actualizar Módulo'}
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleContinue}
                  className="w-full bg-blue-600 text-white px-3 py-2 text-xs rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Continuar
                </button>
              )}
            </div>
          </div>
        </section>
      </div>
      
      {/* Notification Toast */}
      {notificationConfig && (
        <NotificationToast
          isOpen={showNotification}
          title={notificationConfig.title}
          message={notificationConfig.message}
          type={notificationConfig.type}
          onClose={() => {
            setShowNotification(false);
            setNotificationConfig(null);
          }}
          duration={7000}
        />
      )}
    </div>
  );
};

export default EditModuleDrawer;